import { Client, StompSubscription } from '@stomp/stompjs'
import { useCallback, useEffect, useRef } from 'react'

import { AiJobProgressWebSocketDTO } from 'models/photoStudio'
import { getConversationSocketUrl } from 'utils/conversationSocket/conversationSocketUrl'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'

type JobListener = (frame: AiJobProgressWebSocketDTO) => void

const HEARTBEAT_MS = 10_000
const RECONNECT_DELAY_MS = 5_000

/** Where the platform pushes the progress of an assignment's Photo Studio AI jobs */
export const labJobsTopic = (assignmentId: string) => `/topic/assignments/${assignmentId}/ai-jobs`

const isJobFrame = (payload: unknown): payload is AiJobProgressWebSocketDTO => {
  const { jobId, status } = (payload ?? {}) as Record<string, unknown>
  return typeof jobId === 'string' && (status === 'RUNNING' || status === 'FINISHED' || status === 'FAILED')
}

/**
 * Live progress of the Photo Studio's Lab jobs: one STOMP subscription to the assignment's AI jobs topic, dispatched to
 * listeners by job. Its own client, apart from the conversation socket — a refused SUBSCRIBE closes the whole STOMP
 * session, and would take the conversations down with it. Best effort: the jobs are polled as well, so a socket that
 * is refused is closed for the rest of the session rather than retried.
 */
export const useLabJobSocket = (assignmentId: string | undefined, isEnabled: boolean) => {
  const { getTokenSilently } = useAuth0()
  // The Auth0 provider hands out a new getTokenSilently on each of its renders; a ref keeps the socket up across them
  const getTokenSilentlyRef = useRef(getTokenSilently)
  getTokenSilentlyRef.current = getTokenSilently
  const listenersRef = useRef(new Map<string, Set<JobListener>>())

  useEffect(() => {
    if (!isEnabled || !assignmentId) return undefined
    let subscription: StompSubscription | undefined
    const client = new Client({
      brokerURL: getConversationSocketUrl(),
      reconnectDelay: RECONNECT_DELAY_MS,
      heartbeatIncoming: HEARTBEAT_MS,
      heartbeatOutgoing: HEARTBEAT_MS,
      beforeConnect: async () => {
        try {
          const token = await getTokenSilentlyRef.current()
          client.connectHeaders = token ? { Authorization: `Bearer ${token}` } : {}
        } catch {
          // No token, no socket: the CONNECT is refused and the jobs are polled
          client.connectHeaders = {}
        }
      },
      onConnect: () => {
        subscription = client.subscribe(labJobsTopic(assignmentId), (message) => {
          let payload: unknown
          try {
            payload = JSON.parse(message.body)
          } catch (error) {
            logger.warn('Lab job socket: unreadable progress frame', error, {
              context: { component: 'PhotoStudio', action: 'labJobSocket' },
            })
            return
          }
          if (!isJobFrame(payload)) return
          const frame = payload
          listenersRef.current.get(frame.jobId)?.forEach((listener) => listener(frame))
        })
      },
      onStompError: (frame) => {
        // stompjs would reconnect into the same refusal every few seconds (QA-384); polling carries the jobs instead
        logger.warn('Lab job socket refused, polling only', new Error(frame.headers.message ?? 'STOMP error'), {
          context: { component: 'PhotoStudio', action: 'labJobSocket' },
        })
        void client.deactivate()
      },
    })
    client.activate()
    return () => {
      subscription?.unsubscribe()
      void client.deactivate()
    }
  }, [assignmentId, isEnabled])

  /** Listens to one job's frames; returns the function that stops listening */
  const watchJob = useCallback((jobId: string, listener: JobListener) => {
    const listeners = listenersRef.current.get(jobId) ?? new Set<JobListener>()
    listeners.add(listener)
    listenersRef.current.set(jobId, listeners)
    return () => {
      listeners.delete(listener)
      if (listeners.size === 0) listenersRef.current.delete(jobId)
    }
  }, [])

  return { watchJob }
}
